<script setup lang="ts">
import type { AggregationTermEntry } from '~/models/Content/Response'
import { onMounted, ref } from 'vue'
import { faMinus, faPlus } from '@fortawesome/duotone-regular-svg-icons'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'
import AggregationButton from '~/components/search/AggregationButton.vue'

const props = withDefaults(defineProps<{
  aggregations: AggregationTermEntry[]
  title: string
  criterions: string[]
  isRefetching: boolean
  translator: (a: AggregationTermEntry) => string
  field?: string
  openOnMount?: boolean
}>(), {
  openOnMount: false
})

const emit = defineEmits(['toggleCriterion'])

const open = ref(false)

const isChecked = (entryKey: string) => props.criterions.includes(entryKey) || (props.field && props.criterions.includes(props.field))
const handleCheckboxClick = (entryKey: string) => emit('toggleCriterion', entryKey)

onMounted(() => {
  if (props.criterions.length || props.openOnMount) {
    open.value = true
  }
})
</script>

<template>
  <div class="rounded-xl bg-white">
    <button
      class="group flex w-full cursor-pointer items-center justify-between rounded px-4 py-3 focus-visible:ring"
      @click="open = !open"
    >
      <span
        class="font-bold underline-offset-4 group-hover:underline"
        v-text="title"
      />
      <font-awesome-icon
        :icon="open ? faMinus : faPlus"
        :spin="open"
        style="--fa-animation-duration: 200ms; --fa-animation-iteration-count: 1;"
      />
    </button>
    <Transition
      :duration="500"
      name="nested"
    >
      <div
        v-show="open"
        class="outer border-t-gray-10 mx-4 border-t py-3"
      >
        <ul class="inner flex flex-col gap-2">
          <li
            v-for="(entry) in aggregations"
            :key="entry.key"
          >
            <AggregationButton
              :name="translator(entry)"
              :entry="entry"
              type="label"
              :disabled="isRefetching"
            >
              <AuCheckbox
                :id="entry.key"
                :model-value="!!isChecked(entry.key)"
                :disabled="isRefetching"
                @update:model-value="handleCheckboxClick(entry.key)"
              />
            </AggregationButton>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: all 0.3s linear;
  max-height: 1000px;
}
/* delay leave of parent element */
.nested-leave-active {
  transition-delay: 0.2s;
}

.nested-enter-from,
.nested-leave-to {
  transform: translateY(-5px);
  opacity: 0;
  max-height: 0;
}

.nested-enter-active .inner,
.nested-leave-active .inner {
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  will-change: transform, opacity;
}
/* delay enter of nested element */
.nested-enter-active .inner {
  transition-delay: 0.2s;
}

.nested-enter-from .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}

.nested-leave-to .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}
</style>
